"use client";
import PageTitle from "@/components/common-components/page-title/PageTitle";
import { EyeIcon } from "@/utlility/icons";
import React, { useEffect, useState } from "react";
import "react-image-gallery/styles/css/image-gallery.css";
import "./index.css";
import { Container, Modal } from "react-bootstrap";
import { colors } from "@/app/Colors";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import dynamic from "next/dynamic";

const ImageGallery = dynamic(() => import("react-image-gallery"), {
  ssr: false,
  loading: () => <div className="text-center py-5">Loading Gallery...</div>,
});

const PatientGallery = ({ initialData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const searchParams = useSearchParams();
  const tabParam = searchParams.get("tab");
  const router = useRouter();

  const [activeTab, setActiveTab] = useState("image");

  // Use initialData passed from Server Component
  const galleriesData = initialData;
  const imageData =
    galleriesData?.data?.data?.filter((item) => item.type === "image") || [];
  const videoData =
    galleriesData?.data?.data?.filter((item) => item.type === "video") || [];

  useEffect(() => {
    if (tabParam === "video") setActiveTab("video");
    else setActiveTab("image");
  }, [tabParam]);

  const images = imageData.map((item) => ({
    original: item?.gallery_image,
    thumbnail: item?.gallery_image,
    // description: item?.gallery_title,
  }));

  const getEmbedLink = (url) => {
    if (!url) return "";
    try {
      const urlObj = new URL(url);
      const videoId = urlObj.searchParams.get("v");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      }
      if (url.includes("/embed/")) return `${url}?autoplay=1`;
      return "";
    } catch (err) {
      return "";
    }
  };

  const getThumbnailUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const videoId = urlObj.searchParams.get("v");
      if (videoId) return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      // Fallback or handle standard embed URLs if possible, strict youtube handling for now
      return "";
    } catch (e) { return ""; }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    router.push(`/gallery?tab=${tab}`, { scroll: false });
  };

  const VideoItem = ({ item }) => {
    const [play, setPlay] = useState(false);
    const videoUrl = getEmbedLink(item.video_link);
    const thumbnailUrl = getThumbnailUrl(item.video_link);

    // Fallback if we can't get a thumbnail, just show iframe
    if (!thumbnailUrl) {
      return (
        <div className="video-wrapper position-relative">
          <iframe
            width="100%"
            height="250"
            src={getEmbedLink(item.video_link).replace("?autoplay=1", "")} // No autoplay if static
            title={item.gallery_title}
            frameBorder="0"
            allowFullScreen
            loading="lazy"
          ></iframe>
          <p className="mt-2 fw-medium">{item.gallery_title}</p>
        </div>
      )
    }

    return (
      <div className="video-wrapper position-relative">
        {play ? (
          <iframe
            width="100%"
            height="250"
            src={videoUrl}
            title={item.gallery_title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        ) : (
          <div
            className="video-placeholder cursor-pointer position-relative overflow-hidden rounded-2"
            onClick={() => setPlay(true)}
            style={{ height: '250px' }}
          >
            <Image
              src={thumbnailUrl}
              alt={item.gallery_title}
              fill
              className="object-fit-cover w-100 h-100"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark hover-overlay">
              <div className="play-icon-circle bg-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 60, height: 60 }}>
                <svg width={24} height={24} viewBox="0 0 24 24" fill="currentColor" color={colors['--primary']}>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
        )}
        <p className="mt-2 fw-medium">{item.gallery_title}</p>
      </div>
    );
  };

  return (
    <>
      <PageTitle
        image="/images/contact-bg.webp"
        name="gallery"
        link={"/gallery"}
      />
      <section>
        <Container data-aos="fade">
          <div className="d-flex justify-content-center gap-2 gap-sm-3 gap-md-4">
            <button
              className={`py-2 px-3 tab-images-tags ${activeTab === "image" ? "active-tab" : ""
                }`}
              onClick={() => handleTabChange("image")}
            >
              Images
            </button>

            <button
              className={`py-2 px-3 tab-images-tags ${activeTab === "video" ? "active-tab" : ""}`}
              onClick={() => handleTabChange("video")}
            >
              Videos
            </button>
          </div>

          <div className="grid mt-2 mt-sm-3 mt-md-4 gallery-content mx-auto justify-content-center gap-4">
            {activeTab === "image" ? (
              imageData.length > 0 ? (
                imageData.map((item, i) => (
                  <div className="g-col-12 g-col-md-6 g-col-lg-4" key={i} data-aos="fade">
                    <div className="cursor-pointer position-relative gallery-img" onClick={() => {
                      setIndex(i);
                      setIsOpen(true);
                    }}>
                      <Image
                        src={`${item.gallery_image}`}
                        alt={`${item.gallery_title}`}
                        title={`${item.gallery_title}`}
                        width={414}
                        height={295}
                        priority={i < 6} // Prioritize above the fold images
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-fit-cover w-100 h-100 rounded-2"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className="position-absolute top-0 bottom-0 start-0 end-0 d-flex align-items-center justify-content-center gallery-layar rounded-2">
                        <button className="eye-button">
                          <EyeIcon
                            height={24}
                            width={24}
                            color={colors["--icon"]}
                            strokeWidth="2"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="g-col-12">
                  <div className="text-center py-4">
                    <h3 className="text-muted">No Images Available</h3>
                  </div>
                </div>
              )
            ) : videoData.length > 0 ? (
              videoData.map((item, i) => (
                <div className="g-col-12 g-col-md-6 g-col-lg-4" key={i} data-aos="fade">
                  <VideoItem item={item} />
                </div>
              ))
            ) : (
              <div className="g-col-12">
                <div className="text-center py-4">
                  <h3 className="text-muted">No Videos Available</h3>
                </div>
              </div>
            )}
          </div>
        </Container>
      </section>

      {activeTab === "image" && (
        <Modal
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={isOpen}
          onHide={() => setIsOpen(false)}
        >
          <div className="gallery-imgpreviewer">
            {images?.length > 0 && (
              <ImageGallery
                items={images}
                startIndex={index}
                showThumbnails={true}
                showFullscreenButton={true}
                showPlayButton={false}
                onSlide={(currentIndex) => setIndex(currentIndex)}
                onClick={() => setIsOpen(false)}
              />
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default PatientGallery;
